import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SeoHead from '../components/seo/SeoHead';
import { FAQ_CATEGORIES, ALL_FAQ_ITEMS } from '../config/faq';
import { SUPPORT_EMAIL } from '../config/site';
import './Faq.css';

function getFaqStructuredData() {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: ALL_FAQ_ITEMS.map(item => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
}

const Faq: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    const t = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(t);
  }, []);

  const toggleItem = (id: string) => {
    setOpenId(prev => (prev === id ? null : id));
  };

  return (
    <div className={`faq-page ${visible ? 'faq-page--visible' : ''}`}>
      <SeoHead
        title="FAQ"
        description="Frequently asked questions about DHH Collab — how to record a verse, upload beats, fix microphone issues, verse limits, voting, and more."
        path="/faq"
        keywords="DHH Collab FAQ, record verse help, microphone not working, verse limit, Desi Hip Hop questions"
        jsonLd={getFaqStructuredData()}
      />

      <div className="faq-page__bg" aria-hidden>
        <span className="faq-page__blob faq-page__blob--1" />
        <span className="faq-page__blob faq-page__blob--2" />
        <span className="faq-page__grid" />
      </div>

      <div className="faq-page__inner max-w-4xl mx-auto container-padding">
        <header className="faq-page__header">
          <span className="faq-page__eyebrow">Answers &amp; guidance</span>
          <h1 className="faq-page__title">Frequently Asked Questions</h1>
          <p className="faq-page__lead">
            Everything you need to know about beats, verses, recording, and fixing
            common issues — all in one place.
          </p>
        </header>

        <div className="faq-page__categories">
          {FAQ_CATEGORIES.map((category, categoryIndex) => (
            <section
              key={category.id}
              className="faq-page__category"
              style={{ '--category-i': categoryIndex } as React.CSSProperties}
            >
              <h2 className="faq-page__category-title">
                <span className="faq-page__category-icon" aria-hidden>
                  {category.icon}
                </span>
                {category.title}
              </h2>

              <div className="faq-page__items">
                {category.items.map((item, itemIndex) => {
                  const itemId = `${category.id}-${itemIndex}`;
                  const isOpen = openId === itemId;
                  return (
                    <div
                      key={itemId}
                      className={`faq-page__item ${isOpen ? 'faq-page__item--open' : ''}`}
                    >
                      <button
                        type="button"
                        className="faq-page__question"
                        onClick={() => toggleItem(itemId)}
                        aria-expanded={isOpen}
                        aria-controls={`faq-answer-${itemId}`}
                      >
                        <span className="faq-page__question-text">{item.question}</span>
                        <span className="faq-page__chevron" aria-hidden>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                          </svg>
                        </span>
                      </button>
                      <div
                        id={`faq-answer-${itemId}`}
                        className="faq-page__answer-wrap"
                        role="region"
                        aria-hidden={!isOpen}
                      >
                        <div className="faq-page__answer">
                          <p>{item.answer}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        <div className="faq-page__footer-card">
          <h2 className="faq-page__footer-title">Still have a question?</h2>
          <p className="faq-page__footer-text">
            If you couldn&apos;t find an answer here, reach out to us at{' '}
            <span className="faq-page__footer-email">{SUPPORT_EMAIL}</span> — screenshots
            and your username help us resolve issues faster.
          </p>
          <Link to="/contact" className="faq-page__footer-btn">
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Faq;
